/*
  Counting Macros order rules.

  The single place the theme decides whether a cart may go to checkout, and the
  single way the theme adds an extra meal to a box that is already in the cart.
  Every checkout button, wallet button and cart recommendation relies on this file.

  Rules
    - One-time meals: at least 10.
    - Membership meals: at least 8, all for one delivery week, and every
      membership line must carry the accepted membership agreement.
    - A cart holds one kind only: membership meals and one-time meals cannot
      be checked out together.

  The same rules are rendered on the server in sections/cart-template.liquid so
  the cart page is already correct before this script runs. Keep them in sync.

  This is theme-level enforcement. Enforcing it inside Shopify's own checkout
  requires a checkout validation app (see the notes that came with this change).
*/
(() => {
  if (window.CMOrderRules) return;

  const ONE_TIME_MINIMUM = 10;
  const MEMBERSHIP_MINIMUM = 8;
  const WEEK_PROPERTY = '_cm_delivery_week';
  const AGREEMENT_PROPERTY = '_cm_membership_agreement';
  const routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const originalFetch = window.fetch.bind(window);

  let lastResult = null;
  let refreshTimer = null;
  let bypassNextSubmit = false;

  const moreMeals = (count) => `${count} more ${count === 1 ? 'meal' : 'meals'}`;
  const weekOf = (item) => (item?.properties?.[WEEK_PROPERTY] === 'week-2' ? 'week-2' : 'week-1');

  function evaluate(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    let oneTimeCount = 0;
    let membershipCount = 0;
    let agreementMissing = false;
    const membershipWeeks = new Set();

    for (const item of items) {
      const quantity = Number(item.quantity) || 0;
      if (item.selling_plan_allocation) {
        membershipCount += quantity;
        membershipWeeks.add(weekOf(item));
        if (item.properties?.[AGREEMENT_PROPERTY] !== 'accepted') agreementMissing = true;
      } else {
        oneTimeCount += quantity;
      }
    }

    const orderType = membershipCount > 0 && oneTimeCount === 0 ? 'membership' : 'one-time';
    const problems = [];

    // A cart holds either a membership box or a one-time order, never both.
    if (membershipCount > 0 && oneTimeCount > 0) {
      problems.push(`Your cart has both membership meals and one-time meals. Please check out one type at a time: remove the ${oneTimeCount} one-time ${oneTimeCount === 1 ? 'meal' : 'meals'} to continue with your membership, or remove the membership meals to place a one-time order.`);
    } else if (membershipCount > 0) {
      if (membershipWeeks.size > 1) {
        problems.push('Start your weekly membership with either Week 1 or Week 2, not both in the same checkout.');
      } else if (membershipCount < MEMBERSHIP_MINIMUM) {
        problems.push(`Add ${moreMeals(MEMBERSHIP_MINIMUM - membershipCount)} to your membership box to unlock checkout. Membership boxes need at least ${MEMBERSHIP_MINIMUM} meals.`);
      } else if (agreementMissing) {
        problems.push('Return to Build Your Box and agree to the recurring weekly membership before checking out.');
      }
    }

    if (membershipCount === 0 && oneTimeCount > 0 && oneTimeCount < ONE_TIME_MINIMUM) {
      problems.push(`Add ${moreMeals(ONE_TIME_MINIMUM - oneTimeCount)} to unlock checkout. One-time orders need at least ${ONE_TIME_MINIMUM} meals.`);
    }

    return {
      allowed: problems.length === 0,
      orderType,
      oneTimeCount,
      membershipCount,
      agreementMissing,
      message: problems[0] || '',
      problems
    };
  }

  // Consent record. When a cart holds an agreed membership box, the exact
  // wording the customer agreed to and when they agreed are saved as a cart
  // attribute. Shopify copies cart attributes onto the order, so every
  // membership order shows it under "Additional details" in Shopify admin.
  const AGREEMENT_ATTRIBUTE = 'Membership agreement';
  const AGREEMENT_VERSION = 'v1';
  const AGREEMENT_TEXT = 'I agree to recurring weekly charges for this meal membership until it is canceled.';
  let agreementSync = null;

  function storedAgreement() {
    try {
      for (let index = 0; index < sessionStorage.length; index += 1) {
        const key = sessionStorage.key(index);
        if (!key || !key.startsWith('cm-membership-agreement:')) continue;
        const value = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (value && value.accepted === true) return value;
      }
    } catch {
      // Storage unavailable: the record falls back to the time it was confirmed in the cart.
    }
    return null;
  }

  function agreementRecord() {
    const stored = storedAgreement();
    const how = stored?.source === 'active-membership'
      ? 'existing active membership'
      : 'agreed on Build Your Box';
    const when = stored?.acceptedAt || new Date().toISOString();
    return `Accepted "${AGREEMENT_TEXT}" (${how}, ${when}, agreement ${AGREEMENT_VERSION})`;
  }

  async function syncAgreementRecord(cart, result) {
    const current = String(cart?.attributes?.[AGREEMENT_ATTRIBUTE] || '');
    let next = current;
    if (result.membershipCount > 0 && !result.agreementMissing) {
      if (!current) next = agreementRecord();
    } else if (current) {
      next = '';
    }
    if (next === current) return;

    const response = await originalFetch(`${routesRoot}cart/update.js`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      credentials: 'same-origin',
      body: JSON.stringify({attributes: {[AGREEMENT_ATTRIBUTE]: next}})
    });
    if (!response.ok) throw new Error('The membership agreement could not be saved to the cart.');
  }

  // Make sure every checkout button (cart page and cart drawer) has a visible
  // note explaining why it is locked.
  function ensureNotices() {
    document.querySelectorAll('button[name="checkout"]').forEach((button) => {
      const container = button.closest('[data-cm-checkout-gate], .m-cart-drawer__footer, form') || button.parentElement;
      if (!container || container.querySelector('[data-cm-checkout-minimum]')) return;
      const notice = document.createElement('p');
      notice.className = 'cm-checkout-minimum';
      notice.dataset.cmCheckoutMinimum = '';
      notice.setAttribute('role', 'status');
      notice.hidden = true;
      const anchor = button.closest('.m-cart__checkout-button') || button;
      anchor.parentElement.insertBefore(notice, anchor);
    });
  }

  function apply(result) {
    const locked = !result.allowed;
    ensureNotices();

    document.querySelectorAll('button[name="checkout"]').forEach((button) => {
      if (locked) {
        button.dataset.cmMinimumLocked = 'true';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('title', result.message);
        button.classList.add('cm-checkout-locked');
      } else if (button.dataset.cmMinimumLocked === 'true' || button.classList.contains('cm-checkout-locked')) {
        // Only re-enable buttons these rules disabled, so the theme's own
        // "agree to terms" checkbox keeps control of its button.
        delete button.dataset.cmMinimumLocked;
        button.classList.remove('cm-checkout-locked');
        button.removeAttribute('aria-disabled');
        button.removeAttribute('title');
        const termsBox = button.closest('form, [data-cm-checkout-gate], .m-cart-drawer__footer')
          ?.querySelector('input[name="agree_terms"]');
        button.disabled = Boolean(termsBox && !termsBox.checked);
      }
    });

    document.querySelectorAll('.additional-checkout-buttons').forEach((wallets) => {
      wallets.hidden = locked;
      if (locked) wallets.setAttribute('aria-hidden', 'true');
      else wallets.removeAttribute('aria-hidden');
    });

    document.querySelectorAll('[data-cm-checkout-minimum]').forEach((notice) => {
      notice.hidden = !locked;
      notice.textContent = result.message;
    });

    document.querySelectorAll('[data-cm-checkout-gate]').forEach((gate) => {
      gate.dataset.cmOrderType = result.orderType;
      gate.dataset.cmMealCount = String(result.oneTimeCount + result.membershipCount);
      gate.dataset.cmCheckoutAllowed = String(result.allowed);
    });
  }

  async function getCart() {
    const response = await originalFetch(`${routesRoot}cart.js`, {
      headers: {Accept: 'application/json'},
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('The cart could not be loaded.');
    return response.json();
  }

  async function refresh() {
    const cart = await getCart();
    lastResult = evaluate(cart);
    apply(lastResult);
    try {
      agreementSync = agreementSync || syncAgreementRecord(cart, lastResult);
      await agreementSync;
    } catch (error) {
      console.warn(error);
    } finally {
      agreementSync = null;
    }
    document.dispatchEvent(new CustomEvent('cm:order-rules', {detail: lastResult}));
    return lastResult;
  }

  function scheduleRefresh(delay = 60) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => {
      refresh().catch((error) => console.warn('Counting Macros order rules could not refresh.', error));
    }, delay);
  }

  function showBlockedMessage(result) {
    apply(result);
    const notice = document.querySelector('[data-cm-checkout-minimum]:not([hidden])');
    if (notice) {
      notice.scrollIntoView({behavior: 'smooth', block: 'center'});
    } else {
      // No notice on this page (for example a product page): send them to the
      // cart, which explains what is missing.
      window.location.assign(`${routesRoot}cart`);
    }
  }

  // Re-check the live cart the moment someone tries to check out, so a stale
  // page, a slow load or a button re-enabled by other code cannot skip the rules.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const submitter = event.submitter;
    const goingToCheckout = submitter?.name === 'checkout'
      || /\/checkout/.test(form.getAttribute('action') || '');
    if (!goingToCheckout) return;
    if (bypassNextSubmit) {
      bypassNextSubmit = false;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    refresh()
      .then((result) => {
        if (!result.allowed) return showBlockedMessage(result);
        bypassNextSubmit = true;
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
        else window.location.assign(`${routesRoot}checkout`);
      })
      .catch(() => window.location.assign(`${routesRoot}cart`));
  }, true);

  // Direct links to /checkout go through the same check.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin || !/\/checkout(\/|$)/.test(url.pathname)) return;

    event.preventDefault();
    refresh()
      .then((result) => {
        if (!result.allowed) return showBlockedMessage(result);
        window.location.assign(url.toString());
      })
      .catch(() => window.location.assign(`${routesRoot}cart`));
  }, true);

  // Refresh whenever anything on the page changes the cart.
  window.fetch = function countingMacrosFetch(input, init) {
    const request = originalFetch(input, init);
    const url = typeof input === 'string' ? input : input?.url || '';
    if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
      request.then(() => scheduleRefresh(), () => scheduleRefresh());
    }
    return request;
  };
  document.addEventListener('cart:updated', () => scheduleRefresh());
  window.addEventListener('pageshow', () => scheduleRefresh(0));

  // Adds a meal to the box the customer is already building. In a membership
  // cart the meal goes on the same weekly plan, delivery week and agreement as
  // the rest of the box, and is refused if that meal is not offered on the plan.
  async function addMeal({variantId, productUrl, quantity = 1, week = ''}) {
    const cart = await getCart();
    const current = evaluate(cart);
    const membership = current.orderType === 'membership';
    const sameKind = cart.items.filter((item) => Boolean(item.selling_plan_allocation) === membership);
    const matchingLine = sameKind.find((item) => !week || weekOf(item) === week) || sameKind[0];

    const line = {id: Number(variantId), quantity};
    if (matchingLine?.properties && Object.keys(matchingLine.properties).length) {
      line.properties = {...matchingLine.properties};
    }

    if (membership && matchingLine) {
      const planId = matchingLine.selling_plan_allocation.selling_plan.id;
      const productPath = new URL(productUrl, window.location.origin).pathname.replace(/\/$/, '');
      const productResponse = await originalFetch(`${productPath}.js`, {headers: {Accept: 'application/json'}});
      if (!productResponse.ok) throw new Error('This meal could not be loaded.');
      const product = await productResponse.json();
      const offersPlan = (product.selling_plan_groups || [])
        .some((group) => (group.selling_plans || []).some((plan) => plan.id === planId));
      if (!offersPlan) {
        const error = new Error('This meal is not available on the weekly membership yet.');
        error.code = 'PLAN_UNAVAILABLE';
        throw error;
      }
      line.selling_plan = planId;
    }

    const response = await originalFetch(`${routesRoot}cart/add.js`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      credentials: 'same-origin',
      body: JSON.stringify({items: [line]})
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.description || payload.message || 'The meal could not be added.');
    scheduleRefresh(0);
    return payload;
  }

  // Cart "Recommended meals" buttons. Registered on window in the capture phase
  // so it runs before the older handlers in cart-nutrition.js and
  // cart-week-nutrition.js, which added meals without the membership plan.
  window.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-cm-add-recommendation]');
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.disabled) return;

    const card = button.closest('.cm-recommendation-card');
    const productUrl = card?.querySelector('a[href]')?.getAttribute('href') || '';
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    addMeal({
      variantId: button.dataset.variantId,
      productUrl,
      week: button.dataset.cmDeliveryWeek || ''
    })
      .then(() => {
        button.textContent = 'Added';
        window.location.reload();
      })
      .catch((error) => {
        console.error('Recommended meal could not be added.', error);
        button.disabled = error.code === 'PLAN_UNAVAILABLE';
        button.textContent = error.code === 'PLAN_UNAVAILABLE' ? 'Not on membership yet' : 'Try again';
        if (error.code !== 'PLAN_UNAVAILABLE') {
          window.setTimeout(() => { button.textContent = originalText; }, 1800);
        }
      });
  }, true);

  window.CMOrderRules = {
    evaluate,
    refresh,
    addMeal,
    get current() {
      return lastResult;
    }
  };

  function start() {
    scheduleRefresh(0);
    const events = window.MinimogEvents;
    const cartUpdate = window.MinimogTheme?.pubSubEvents?.cartUpdate;
    if (events?.subscribe && cartUpdate) events.subscribe(cartUpdate, () => scheduleRefresh());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {once: true});
  } else {
    start();
  }
})();
